import { useCallback, useEffect, useState } from 'react';
import LoginLayout from '@src/layout/LoginLayout';
import {
  CustomButton,
  CustomForm,
  CustomFormItem,
  CustomImage,
  CustomPassword,
  LoadingImage,
  Text,
  useText,
} from '@src/components';
import formRule from '@src/utils/formRule';
import approveIcon from '@src/assets/icons/icon-reset-pass-approve.svg';
import errorIcon from '@src/assets/icons/icon-reset-pass-error.svg';
import '@src/styles/login/resetPassword.scss';
import { useDispatch } from 'react-redux';
import { forgotPasswordChange, forgottenPasswordTokenCheck } from '@src/store/slice/authSlice';
import { passwordRegex } from '@src/utils/utils';

const ResetPassword = ({ history }) => {
  const dispatch = useDispatch();
  const loginPasswordInput = useText('loginPasswordInput');
  const enterPasswordAgain = useText('enterPasswordAgain');

  const [passwordError, setPasswordError] = useState({
    visible: false,
    messageList: [],
    isValid: false,
    errorList: [],
  });
  const [resetToken, setResetToken] = useState('');

  const passwordRules = useCallback(async () => {
    const data = passwordRegex;
    const messageList = [];
    data?.forEach((item) => {
      messageList?.push({ type: 'error', message: item?.text });
    });
    setPasswordError({ visible: true, messageList, isValid: false, errorList: data });
  }, [dispatch, history]);

  useEffect(() => {
    const params = new URLSearchParams(window?.location?.search);
    const paramToken = params?.get('token');
    if (paramToken === null || paramToken === '') {
      history?.push('login');
    } else {
      (async () => {
        const action = await dispatch(forgottenPasswordTokenCheck({ token: paramToken }));
        if (forgottenPasswordTokenCheck.fulfilled.match(action)) {
          await passwordRules();
          setResetToken(paramToken);
        } else {
          history?.push('/forgot-password?error=true');
        }
      })();
    }
  }, [history, dispatch, passwordRules]);

  const onFinish = useCallback(
    async (values) => {
      if (passwordError.isValid) {
        const body = {
          token: resetToken,
          newPassword: values?.password,
        };
        const action = await dispatch(forgotPasswordChange(body));
        if (forgotPasswordChange.fulfilled.match(action)) {
          history?.push('/sms-verification');
        } else if (
            typeof action?.payload?.error?.message === 'string' &&
            action?.payload?.error?.message?.includes('*Validation Error')
        ) {
          const messageList = [];
          const validations = [];
          passwordError?.errorList?.forEach((item) => {
            if (action?.payload?.indexOf(item?.text) !== -1) {
              messageList?.push({type: 'error', message: item?.text});
              validations.push(true);
            } else {
              messageList?.push({type: 'success', message: item?.text});
              validations.push(false);
            }
          });
          setPasswordError({
            ...passwordError,
            visible: true,
            messageList,
            isValid: !validations.includes(true),
          });
        }
      }
    },
    [passwordError, resetToken, dispatch, history],
  );

  const regexErrorItemResult = (item = {}, value = '') => {
    const regex = new RegExp(item?.regExp);
    if (regex.test(value)) {
      return { listItem: { type: 'success', message: item?.text }, validation: false };
    } else {
      return { listItem: { type: 'error', message: item?.text }, validation: true };
    }
  };

  const resultErrorList = (list, value) => {
    const messageList = [];
    const validations = [];
    list?.forEach((item = { regExp: '', text: '' }) => {
      const { listItem, validation } = regexErrorItemResult(item, value);
      messageList.push(listItem);
      validations.push(validation);
    });

    return { messageList, validations };
  };

  const onChange = (e) => {
    const value = e?.target?.value;

    const { messageList, validations } = resultErrorList(passwordError.errorList, value);

    setPasswordError({
      ...passwordError,
      visible: messageList.length > 0,
      messageList,
      isValid: !validations.includes(true),
    });
  };

  return resetToken === '' ? (
    <LoadingImage />
  ) : (
    <LoginLayout isBackButton={true} className={'resetPassword'}>
      <div className="rightSideLoginFormTitle">
        <Text t="renewPassword" />
      </div>

      <CustomForm
        name="resetPassword"
        initialValues={{}}
        onFinish={onFinish}
        autoComplete="off"
        layout={'vertical'}
      >
        <CustomFormItem
          label={<Text t="yourNewPassword" />}
          name="password"
          rules={[{ required: false, max: 20, message: <Text t="checkPassword" /> }]}
        >
          <CustomPassword id="resetPassword-main-password-passwordInput" placeholder={loginPasswordInput} onChange={onChange} maxLength={20} />
        </CustomFormItem>

        <CustomFormItem
          label={<Text t="yourAgainNewPassword" />}
          name="confirmPassword"
          dependencies={['password']}
          rules={[
            { required: true, max: 20, message: <Text t="checkPassword" /> },
            (form) =>
              formRule.formStringMatching(form, 'password', <Text t="twoPasswordDontMatch" />),
          ]}
        >
          <CustomPassword id="resetPassword-main-confirmPassword-passwordInput" placeholder={enterPasswordAgain} autoComplete="off" maxLength={20} />
        </CustomFormItem>

        {passwordError.visible && (
          <div className="passwordMessageContent">
            <div className="text">
              <Text t="yourPassword" />
            </div>
            {passwordError.messageList?.map((item, index) => {
              return (
                <div key={`reset-pass-error-list-${item?.message}`} className="passwordWarning">
                  <CustomImage
                    className="icons"
                    src={item?.type === 'error' ? errorIcon : approveIcon}
                  />
                  <span className={item?.type}>{item?.message}</span>
                </div>
              );
            })}
            <div className="text">
              <Text t="mustContain" />
            </div>
          </div>
        )}
        <CustomFormItem>
          <CustomButton id="resetPassword-main-save-primary-btn" type="primary" htmlType="submit" style={{ width: '100%' }}>
            <span className="submit">
              <Text t="saveBtn" />
            </span>
          </CustomButton>
        </CustomFormItem>
      </CustomForm>
    </LoginLayout>
  );
};

export default ResetPassword;
